import { useEffect } from 'react';
import { LuChevronLeft, LuAlertCircle } from 'react-icons/lu';
import LazyLoad from 'react-lazyload';
import { Link, useNavigate } from 'react-router-dom';
import { setUnreadNotificationsAsRead } from '../../api-services/notifications/useClearUnreadNotifications';
import { useFetchNotifications } from '../../api-services/notifications/useFetchNotifications';
import { SITELINK } from '../../app-routes/Links';
import { generateRandomHexColor, getFirstLetters } from '../../app-utils';
import { Verify } from '../../assets/custom-icons';
import { EmptyNotification } from '../../assets/images';
import { useTheme } from '../../context/theme-context';

// Skeleton Component
const NotificationSkeleton = () => {
  const { isDarkMode } = useTheme();
  
  return (
    <div className="w-full py-2 border-b last:border-b-0 px-3 animate-pulse">
      <div className="flex gap-2">
        {/* Avatar skeleton */}
        <div className={`size-10 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
        
        <div className="flex-1">
          <div className="w-full flex items-center justify-between">
            {/* Name skeleton */}
            <div className={`h-4 w-32 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
            {/* Time skeleton */}
            <div className={`h-3 w-16 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
          </div>
          {/* Message skeleton */}
          <div className={`h-3 w-48 rounded mt-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
        </div>
      </div>
    </div>
  );
};

// Error Component
const NotificationsError = ({ onRetry, error }: { onRetry: () => void; error: any }) => {
  const { isDarkMode } = useTheme();
  
  return (
    <div className={`flex flex-col items-center justify-center min-h-[60vh] p-6`}>
      <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
        isDarkMode ? 'bg-red-900/30' : 'bg-red-100'
      }`}>
        <LuAlertCircle className={`w-10 h-10 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
      </div>
      <h3 className={`text-lg font-semibold mb-2 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
        Failed to load notifications
      </h3>
      <p className={`text-sm mb-6 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        {error?.message || 'Unable to load notifications. Please check your connection.'}
      </p>
      <button
        onClick={onRetry}
        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        Try Again
      </button>
    </div>
  );
};

// Empty State Component
const NotificationsEmpty = () => {
  const { isDarkMode } = useTheme();
  
  return (
    <div className="relative top-[50px] py-3">
      <div className="text-center flex flex-col gap-3 items-center justify-center">
        <div className="w-[350px] aspect-square flex items-center justify-center">
          <img src={EmptyNotification} className='object-cover' alt='empty_notification' />
        </div>
        <p className={`text-xs flex flex-col font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No notifications available
        </p>
        <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          When you get notifications, they'll appear here
        </p>
      </div>
    </div>
  );
};

// main-component-ui
const Notifications = () => {
  document.body.classList.remove('overflow-hidden');
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  
  const notification_data = useFetchNotifications();

  useEffect(() => {
    setUnreadNotificationsAsRead();
  }, []);

  const handleRetry = async () => {
    await notification_data.refetch();
  };

  // Show loading skeleton
  if (notification_data.isLoading) {
    return (
      <section className={`min-h-screen ${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}>
        {/* top lead area */}
        <div className="fixed top-0 w-full z-50 bg-white dark:bg-neutral-800 shadow-sm">
          <div className="flex gap-4 relative px-2 py-3">
            <button
              onClick={() => navigate(-1)}
              className="font-semibold text-sm flex hover:opacity-70 transition-opacity"
              aria-label="Go back"
            >
              <LuChevronLeft className={`text-2xl mt-[2px] ${isDarkMode ? 'text-white' : 'text-gray-800'}`} />
            </button>
            <p className={`font-medium madimi-one ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Notifications
            </p>
          </div>
        </div>

        {/* Content area with skeletons */}
        <div className="mt-[60px]">
          {[1, 2, 3, 4, 5].map((index) => (
            <NotificationSkeleton key={index} />
          ))}
        </div>
      </section>
    );
  }

  // Show error state
  if (notification_data.isError) {
    return (
      <section className={`min-h-screen ${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}>
        <div className="fixed top-0 w-full z-50 bg-white dark:bg-neutral-800 shadow-sm">
          <div className="flex gap-4 relative px-2 py-3">
            <button
              onClick={() => navigate(-1)}
              className="font-semibold text-sm flex hover:opacity-70 transition-opacity"
              aria-label="Go back"
            >
              <LuChevronLeft className={`text-2xl mt-[2px] ${isDarkMode ? 'text-white' : 'text-gray-800'}`} />
            </button>
            <p className={`font-medium madimi-one ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Notifications
            </p>
          </div>
        </div>
        <div className="mt-[60px]">
          <NotificationsError onRetry={handleRetry} error={notification_data.error} />
        </div>
      </section>
    );
  }

  return (
    <section className={`min-h-screen ${isDarkMode ? 'bg-neutral-800' : 'bg-white'}`}>
      {/* top lead area and post btn */}
      <div className="fixed top-0 w-full z-50 bg-white dark:bg-neutral-800 shadow-sm">
        <div className="flex gap-4 relative px-2 py-3">
          <button
            onClick={() => navigate(-1)}
            className="font-semibold text-sm flex hover:opacity-70 transition-opacity"
            aria-label="Go back"
          >
            <LuChevronLeft className={`text-2xl mt-[2px] ${isDarkMode ? 'text-white' : 'text-gray-800'}`} />
          </button>

          <p className={`font-medium madimi-one ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            Notifications
          </p>
        </div>
      </div>

      {/* content area */}
      <div className="mt-[60px]">
        <div className='w-full dark:bg-neutral-800'>
          {notification_data?.data?.length > 0 ? (
            notification_data?.data?.map((notification: any, index: number) => (
              <div 
                key={notification.id || index}
                onClick={() => navigate(`${SITELINK.VIEW_POSTS}/${notification?.post_id}`)} 
                className={`w-full py-3 border-b last:border-b-0 px-3 cursor-pointer transition-colors ${
                  isDarkMode 
                    ? 'border-gray-700 hover:bg-neutral-700' 
                    : 'border-gray-100 hover:bg-gray-50'
                }`}
              >
                <div className="flex gap-3">
                  {/* Avatar */}
                  <Link
                    to={`${SITELINK.MY_PROFILE}/${notification?.creator?.username}`}
                    onClick={(e) => e.stopPropagation()}
                    className="shrink-0"
                  >
                    {notification?.creator?.profile_picture_url ? (
                      <LazyLoad height={40} offset={1000} once>
                        <img
                          alt="profile"
                          src={notification?.creator?.profile_picture_url}
                          className="object-cover rounded-full size-10 bg-gray-200 shadow-sm overflow-hidden"
                        />
                      </LazyLoad>
                    ) : (
                      <div
                        className="flex items-center justify-center size-10 rounded-full border border-green-100"
                        style={{ background: generateRandomHexColor() }}
                      >
                        <span className="text-white uppercase text-sm font-semibold">
                          {getFirstLetters(notification?.creator?.username || 'U')}
                        </span>
                      </div>
                    )}
                  </Link>

                  {/* Content */}
                  <div className='flex-1 min-w-0'>
                    <div className="w-full flex items-center justify-between gap-2">
                      <p className={`text-sm font-semibold flex items-center gap-1 truncate ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        <span className="madimi-one truncate">
                          {notification?.creator?.first_name ? notification?.creator?.first_name + ' ' + notification?.creator?.last_name : 'Anonymous'}
                        </span>
                        <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          @{notification?.creator?.username ? notification?.creator?.username : 'anonymous'}
                        </span>
                        {notification?.creator?.is_verified && (
                          <img src={Verify} alt="Verified" className="h-3 w-3 shrink-0" />
                        )}
                      </p>
                      <p className={`text-[10px] font-medium shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {notification?.created}
                      </p>
                    </div>
                    <p className={`text-xs font-medium mt-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      {notification.post_reaction}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <NotificationsEmpty />
          )}
        </div>
      </div>
    </section>
  );
};

export default Notifications;